import React, { useEffect, useRef, useState } from "react";
import { FaTimes } from "react-icons/fa";
import {
  CUSTOM_SQUAD_TIME,
  NOON_SQUAD_TIME,
  SQUAD_TIME_OPTIONS,
  normalizeSquadTime,
  squadTimeLabel,
} from "../../utility/createSquad";

export const TimeChip = ({ time, onRemove }) => (
  <span className="inline-flex items-center gap-1 h-8 pl-3 pr-1 rounded-full bg-brand-charcoal text-brand-cream text-sm font-medium">
    {squadTimeLabel(time)}
    <button
      type="button"
      onClick={onRemove}
      title={`Remove ${squadTimeLabel(time)}`}
      className="inline-flex items-center justify-center w-6 h-6 rounded-full hover:bg-white/15"
    >
      <FaTimes className="h-2.5 w-2.5" />
    </button>
  </span>
);

export const AlsoEventChip = ({
  eventName,
  checked,
  onToggle,
  disabled = false,
}) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onToggle}
    aria-pressed={checked}
    className={`h-8 px-3 rounded-full text-sm font-medium disabled:cursor-not-allowed ${
      checked
        ? "bg-brand-cream border border-brand-charcoal text-brand-charcoal"
        : "border border-dashed border-brand-charcoal/30 bg-transparent text-brand-muted hover:border-brand-charcoal hover:text-brand-charcoal"
    }`}
  >
    Also {eventName}
  </button>
);

export const SuggestedTimeChip = ({ time, onAdd, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={() => onAdd(time)}
    className="h-8 px-3 rounded-full border border-dashed border-brand-charcoal/30 bg-transparent text-sm text-brand-muted hover:border-brand-charcoal hover:text-brand-charcoal disabled:cursor-not-allowed"
  >
    Add {squadTimeLabel(time)}
  </button>
);

export const AddTimeTrigger = ({ exclude, onAdd, disabled }) => {
  const [open, setOpen] = useState(false);
  const [useCustom, setUseCustom] = useState(false);
  const [customValue, setCustomValue] = useState(NOON_SQUAD_TIME);
  const rootRef = useRef(null);
  const noonRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    noonRef.current?.scrollIntoView({ block: "center" });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  const addCustom = () => {
    const next = normalizeSquadTime(customValue);
    if (!next || exclude.includes(next)) {
      setUseCustom(false);
      setCustomValue(NOON_SQUAD_TIME);
      return;
    }
    onAdd(next);
    setUseCustom(false);
    setCustomValue(NOON_SQUAD_TIME);
  };

  if (useCustom) {
    return (
      <span className="inline-flex items-center gap-1">
        <input
          type="time"
          autoFocus
          disabled={disabled}
          value={customValue}
          aria-label="Custom squad time"
          onChange={(event) => setCustomValue(event.target.value)}
          onBlur={addCustom}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addCustom();
            }
            if (event.key === "Escape") {
              setUseCustom(false);
              setCustomValue(NOON_SQUAD_TIME);
            }
          }}
          className="h-8 rounded-full border border-brand-charcoal/20 bg-white px-3 text-sm text-brand-charcoal focus:outline-none focus:ring-2 focus:ring-brand-gold-ink"
        />
      </span>
    );
  }

  const options = SQUAD_TIME_OPTIONS.filter(
    (option) => !exclude.includes(option.value)
  );

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label="Add time"
        onClick={() => setOpen((value) => !value)}
        className="h-8 px-3 rounded-full border border-dashed border-brand-charcoal/30 bg-transparent text-sm text-brand-muted hover:border-brand-charcoal hover:text-brand-charcoal cursor-pointer focus:outline-none focus:ring-2 focus:ring-brand-gold-ink disabled:cursor-not-allowed"
      >
        Add time
      </button>
      {open ? (
        <div
          role="listbox"
          aria-label="Squad times"
          className="absolute z-30 mt-1 w-36 max-h-56 overflow-y-auto bg-white border border-brand-charcoal/15 rounded-lg shadow-lg"
        >
          {options.map((option) => {
            const isNoonAnchor =
              option.value === NOON_SQUAD_TIME ||
              (!options.some((item) => item.value === NOON_SQUAD_TIME) &&
                option.value ===
                  options.find((item) => item.value >= NOON_SQUAD_TIME)
                    ?.value);
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                ref={isNoonAnchor ? noonRef : null}
                onClick={() => {
                  onAdd(option.value);
                  setOpen(false);
                }}
                className="block w-full px-3 py-1.5 text-left text-sm text-brand-charcoal hover:bg-brand-cream"
              >
                {option.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => {
              setOpen(false);
              setUseCustom(true);
            }}
            className="block w-full px-3 py-1.5 text-left text-sm text-brand-muted border-t border-brand-charcoal/10 hover:bg-brand-cream"
          >
            Custom
          </button>
        </div>
      ) : null}
    </div>
  );
};
